import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { api } from '../api';
import { passkeysAvailable, signInWithPasskey, wasCancelled } from '../utils/passkeys';
import { PLATFORM_NAME, PLATFORM_INITIAL, ACCESS_REQUEST_EMAIL } from '../branding';

/** Seconds before a fresh code can be requested, matching nothing on the
 *  server — it only exists to stop impatient double-taps. */
const RESEND_COOLDOWN = 30;

const HIGHLIGHTS = [
  {
    title: 'Tasks & compliance',
    body: 'Statutory due dates, recurring work and sub-tasks tracked to closure.',
  },
  {
    title: 'Billing & GST',
    body: 'Invoices, credit notes, receivables and reconciliation in one ledger.',
  },
  {
    title: 'People & payroll',
    body: 'Attendance, leaves, reimbursements and monthly payroll runs.',
  },
];

type Mode = 'password' | 'code';

const Login: React.FC = () => {
  const { login, loginWithOtp, applySession } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState<Mode>('password');
  const [biometricsOffered, setBiometricsOffered] = useState(false);
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  /** In code mode: false while asking for the address, true once sent. */
  const [codeSent, setCodeSent] = useState(false);
  const [notice, setNotice] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  // Only offer biometrics where the device actually has them. Whether this
  // particular person has enrolled is not knowable before they try — asking
  // the server would mean naming an account before anyone has proved anything.
  useEffect(() => {
    let cancelled = false;
    passkeysAvailable().then((ok) => {
      if (!cancelled) setBiometricsOffered(ok);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const handlePasskey = async () => {
    setError('');
    setNotice('');
    setPasskeyBusy(true);
    try {
      applySession(await signInWithPasskey());
      navigate('/tasks');
    } catch (err: any) {
      // Dismissing the system prompt is a decision, not a failure.
      if (!wasCancelled(err)) {
        setError(
          err.response?.data?.message ||
            'No passkey for this site on this device. Sign in below, then set one up from your profile.',
        );
      }
    } finally {
      setPasskeyBusy(false);
    }
  };

  const switchMode = (next: Mode) => {
    setMode(next);
    setError('');
    setNotice('');
    setCode('');
    setPassword('');
    setCodeSent(false);
  };

  const handlePasswordLogin = async () => {
    await login(email, password);
    navigate('/tasks');
  };

  const handleRequestCode = async () => {
    const res = await api.post('/auth/otp/request', { email });
    // The server answers the same way whether or not the address exists, so
    // there is nothing here to branch on — just show what it said.
    setNotice(res.data?.message || 'If that email has an account, a code is on its way.');
    setCodeSent(true);
    setCooldown(RESEND_COOLDOWN);
  };

  const handleVerifyCode = async () => {
    await loginWithOtp(email, code);
    navigate('/tasks');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (mode === 'password') await handlePasswordLogin();
      else if (!codeSent) await handleRequestCode();
      else await handleVerifyCode();
    } catch (err: any) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.error ||
          (mode === 'password' ? 'Login failed' : 'Something went wrong. Please try again.'),
      );
    } finally {
      setLoading(false);
    }
  };

  const submitLabel = () => {
    if (loading) return mode === 'password' ? 'Signing in...' : 'Please wait...';
    if (mode === 'password') return 'Sign In';
    return codeSent ? 'Verify & Sign In' : 'Email me a code';
  };

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-white">
      {/* ── Brand panel ─────────────────────────────────────────────────── */}
      <div className="relative lg:w-[55%] min-h-[220px] lg:min-h-screen overflow-hidden">
        <img
          src="/images/login-office.jpg"
          alt=""
          aria-hidden="true"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/95 via-slate-900/80 to-blue-900/70" />

        <div className="relative h-full flex flex-col justify-between p-8 lg:p-14 text-white">
          <div className="flex items-center gap-3">
            <div className="inline-flex items-center justify-center w-11 h-11 bg-blue-600 rounded-xl shadow-lg">
              <span className="text-white text-xl font-bold">{PLATFORM_INITIAL}</span>
            </div>
            <span className="text-lg font-semibold tracking-tight">{PLATFORM_NAME}</span>
          </div>

          <div className="hidden lg:block max-w-lg mt-12">
            <h2 className="text-4xl font-bold leading-tight tracking-tight">
              The practice, end to end.
            </h2>
            <p className="mt-4 text-slate-300 leading-relaxed">
              One workspace for the work your firm does, the money it bills and the
              people who deliver it.
            </p>

            <dl className="mt-10 space-y-6">
              {HIGHLIGHTS.map((h) => (
                <div key={h.title} className="flex gap-4">
                  <span className="mt-2 h-1.5 w-1.5 rounded-full bg-blue-400 shrink-0" />
                  <div>
                    <dt className="font-medium">{h.title}</dt>
                    <dd className="text-sm text-slate-300 mt-0.5">{h.body}</dd>
                  </div>
                </div>
              ))}
            </dl>
          </div>

          <p className="hidden lg:block text-xs text-slate-400">
            © {new Date().getFullYear()} {PLATFORM_NAME}. All rights reserved.
          </p>
        </div>
      </div>

      {/* ── Sign-in panel ───────────────────────────────────────────────── */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-10">
        <div className="w-full max-w-sm">
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">Welcome back</h1>
          <p className="text-gray-500 text-sm mt-1">Sign in to continue to your workspace.</p>

          {/* Above the tabs, not a third tab: this is one tap that either
              works or falls through to the tabs below, not a mode you dwell
              in and fill a form under. */}
          {biometricsOffered && (
            <div className="mt-8">
              <button
                type="button"
                onClick={handlePasskey}
                disabled={passkeyBusy}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl border border-gray-300 bg-white text-sm font-semibold text-gray-800 hover:bg-gray-50 disabled:text-gray-400 transition-colors"
              >
                <span aria-hidden="true">🔐</span>
                {passkeyBusy ? 'Waiting for your device…' : 'Sign in with Face ID or fingerprint'}
              </button>
              <div className="flex items-center gap-3 mt-6">
                <span className="flex-1 h-px bg-gray-200" />
                <span className="text-xs text-gray-400">or</span>
                <span className="flex-1 h-px bg-gray-200" />
              </div>
            </div>
          )}

          {/* The indicator is one absolutely-positioned pill that slides,
              rather than a background on the active tab — a moving element
              reads as the same control changing state, where two swapping
              backgrounds read as a repaint. Its width is exactly its own
              travel distance, so translateX(100%) lands it on the second
              segment whatever the container measures. */}
          <div
            role="tablist"
            aria-label="Sign-in method"
            className={`relative grid grid-cols-2 p-1 bg-gray-100 rounded-xl ${
              biometricsOffered ? 'mt-4' : 'mt-8'
            }`}
          >
            <span
              aria-hidden="true"
              className="absolute inset-y-1 left-1 w-[calc(50%-0.25rem)] rounded-lg bg-white shadow-sm transition-transform duration-200 ease-out motion-reduce:transition-none"
              style={{ transform: mode === 'code' ? 'translateX(100%)' : 'translateX(0)' }}
            />
            {([
              { key: 'password', label: 'Password' },
              { key: 'code', label: 'One-time code' },
            ] as const).map((tab) => (
              <button
                key={tab.key}
                type="button"
                role="tab"
                aria-selected={mode === tab.key}
                onClick={() => switchMode(tab.key)}
                className={`relative z-10 py-2 text-sm font-medium rounded-lg transition-colors ${
                  mode === tab.key ? 'text-gray-900' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4 mt-6">
            <div>
              <label className="label">Email Address</label>
              <input
                type="email"
                className="input-field disabled:bg-gray-50 disabled:text-gray-500"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@mgsg.in"
                autoComplete="username"
                // Locked once a code is out: the code was issued for this
                // address, so editing it here would only produce a mismatch.
                disabled={mode === 'code' && codeSent}
                required
              />
            </div>

            {mode === 'password' && (
              <div>
                <label className="label">Password</label>
                <input
                  type="password"
                  className="input-field"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  autoComplete="current-password"
                  required
                />
              </div>
            )}

            {mode === 'code' && codeSent && (
              <div>
                <label className="label">6-Digit Code</label>
                <input
                  type="text"
                  inputMode="numeric"
                  className="input-field text-center text-lg tracking-[0.5em] font-medium"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  autoComplete="one-time-code"
                  autoFocus
                  required
                />
              </div>
            )}

            {notice && !error && (
              <div className="bg-blue-50 text-blue-800 text-sm px-4 py-3 rounded-lg border border-blue-200">
                {notice}
              </div>
            )}

            {error && (
              <div className="bg-red-50 text-red-700 text-sm px-4 py-3 rounded-lg border border-red-200">
                {error}
              </div>
            )}

            <button
              type="submit"
              className="btn-primary w-full py-3 mt-2"
              disabled={loading || (mode === 'code' && codeSent && code.length !== 6)}
            >
              {submitLabel()}
            </button>
          </form>

          {/* Only shown once a code is out: the tabs handle switching method,
              so all that's left here is the two things you might want while
              waiting for an email that hasn't arrived. */}
          {mode === 'code' && codeSent && (
            <div className="mt-4 text-sm flex items-center justify-between gap-4">
              <button
                type="button"
                onClick={() => switchMode('code')}
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                Use a different email
              </button>
              <button
                type="button"
                disabled={cooldown > 0 || loading}
                onClick={() => {
                  setError('');
                  setLoading(true);
                  handleRequestCode()
                    .catch((err: any) =>
                      setError(err.response?.data?.error || 'Could not resend the code'),
                    )
                    .finally(() => setLoading(false));
                }}
                className="text-gray-500 hover:text-gray-700 disabled:text-gray-300 disabled:hover:text-gray-300"
              >
                {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
              </button>
            </div>
          )}

          <div className="mt-8 pt-6 border-t border-gray-200">
            <p className="text-sm text-gray-500 leading-relaxed">
              Don't have an account? Access is issued by your firm's admin —{' '}
              <a
                href={`mailto:${ACCESS_REQUEST_EMAIL}?subject=${encodeURIComponent(
                  `Access request — ${PLATFORM_NAME}`,
                )}`}
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                request access
              </a>
              .
            </p>
          </div>

          <p className="lg:hidden text-xs text-gray-400 mt-10 text-center">
            © {new Date().getFullYear()} {PLATFORM_NAME}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
